import React, { useEffect, useRef, useState } from 'react';
import { nasAPI } from '../services/api';
import { Activity } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// ─── helpers ─────────────────────────────────────────────────────────────────
function fmtMbps(v) {
  if (v === null || v === undefined) return '—';
  if (v >= 100) return `${v.toFixed(0)}`;
  if (v >= 10)  return `${v.toFixed(1)}`;
  return `${v.toFixed(2)}`;
}

function fmtBytes(b) {
  if (!b || b <= 0) return '0 MB';
  const mb = Number(b) / 1048576;
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}

// ─── Inline badge — used in customer list rows ────────────────────────────────
// Props: { rate }  where rate = { rx, tx, online } | null
export function LiveTrafficBadge({ rate }) {
  if (!rate || !rate.online) {
    return (
      <span title="Offline / not connected" style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: '.68rem', color: 'var(--c-94a3b8)',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--cb-cbd5e1)', display: 'inline-block' }} />
        offline
      </span>
    );
  }
  // Netzur-sourced (NAS we can't poll directly, e.g. older RouterOS with no REST API) —
  // accurate online status, but no per-second rate to show (see LiveBandwidthPanel for
  // the full explanation). Showing "0.00 Mbps" here would misread as a stalled link.
  if (rate.source === 'imbs') {
    return (
      <span title="Online (live speed not available for this NAS)" style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '.68rem', color: 'var(--c-16a34a)', fontWeight: 600,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--cb-22c55e)', display: 'inline-block' }} />
        online
      </span>
    );
  }
  const active = rate.rx > 0.01 || rate.tx > 0.01;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.72rem', fontVariantNumeric: 'tabular-nums' }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: active ? 'var(--cb-22c55e)' : 'var(--cb-f59e0b)',
        display: 'inline-block',
        animation: active ? 'livePulse 1.8s ease-out infinite' : 'none',
      }} />
      <span style={{ color: 'var(--primary)', fontWeight: 600 }}>↓{fmtMbps(rate.rx)}</span>
      <span style={{ color: 'var(--c-94a3b8)' }}>·</span>
      <span style={{ color: 'var(--c-ea580c)', fontWeight: 600 }}>↑{fmtMbps(rate.tx)}</span>
      <span style={{ color: 'var(--c-94a3b8)' }}>Mbps</span>
    </span>
  );
}


// ─── Panel — used in customer detail page (admin) and the customer portal ────
// Props: { username, fetchRate, showUsername = true, pollMs = 1000 }
// By default fetches via nasAPI.getSubscriberLive(username) (staffOrAdmin route, arbitrary
// username — admin/staff use only). Pass `fetchRate` (a () => Promise<{data:{data}}> thunk)
// to source the rate from elsewhere instead — e.g. the customer portal's self-scoped
// GET /customers/me/bandwidth, which never accepts a client-supplied username.
export function LiveBandwidthPanel({ username, fetchRate, showUsername = true, pollMs = 1_000, embedded = false }) {
  const [rate, setRate]     = useState(null);
  const [history, setHistory] = useState([]);
  const [lastOk, setLastOk]   = useState(null);   // time of the last successful reading
  const [failed, setFailed]   = useState(false);  // latest request failed
  const [now, setNow]         = useState(Date.now());
  const [winSec, setWinSec]   = useState(30);     // chart window
  const [paused, setPaused]   = useState(false);   // freezes the chart (readings keep updating)
  const pausedRef           = useRef(false);
  pausedRef.current = paused;
  const timerRef            = useRef(null);

  useEffect(() => {
    if (!username && !fetchRate) return;
    let mounted = true;

    const fetch = () => {
      (fetchRate ? fetchRate() : nasAPI.getSubscriberLive(username))
        .then(r => {
          if (!mounted) return;
          const data = r.data.data;
          if (!data) return;
          setRate(data);
          setLastOk(Date.now());
          setFailed(false);
          if (data.online && data.source !== 'imbs' && !pausedRef.current) {
            setHistory(h => {
              const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
              const pt   = { time, rx: data.rx, tx: data.tx };
              const next = [...h, pt];
              return next.length > 300 ? next.slice(-300) : next;
            });
          }
        })
        .catch(() => { if (mounted) setFailed(true); });
    };

    // No point polling a background tab: skip while hidden, refresh the moment it is shown again.
    const tick = () => { if (!document.hidden) fetch(); };
    fetch();
    timerRef.current = setInterval(tick, pollMs);
    document.addEventListener('visibilitychange', tick);
    return () => { mounted = false; clearInterval(timerRef.current); document.removeEventListener('visibilitychange', tick); };
  }, [username, fetchRate, pollMs]);

  // Clock for the "x s ago" label; state only, no request.
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  if (!username && !fetchRate) return null;

  const ageSec = lastOk ? Math.max(0, Math.round((now - lastOk) / 1000)) : null;
  const stale = ageSec !== null && ageSec > Math.max(10, (pollMs / 1000) * 5);
  const online = rate?.online;
  const imbsSourced = rate?.source === 'imbs';
  // Netzur-sourced rates carry rx/tx as null (accounting updates every few minutes, not
  // every second) — we genuinely don't know instantaneous activity for those, so skip
  // the active/idle distinction rather than guess.
  const active = online && !imbsSourced && (rate.rx > 0.01 || rate.tx > 0.01);
  const shown = history.slice(-Math.max(2, Math.round(winSec * 1000 / pollMs)));
  const peakRx = shown.reduce((m, p) => Math.max(m, p.rx || 0), 0);
  const peakTx = shown.reduce((m, p) => Math.max(m, p.tx || 0), 0);
  const winBtn = (on) => ({ font: 'inherit', fontSize: '.7rem', padding: '2px 8px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`, background: on ? 'var(--primary)' : 'transparent', color: on ? 'var(--c-ffffff)' : 'var(--text-muted)' });

  return (
    <div style={{
      border: embedded ? 0 : '1px solid var(--border)', borderRadius: 10, overflow: 'hidden',
      marginBottom: embedded ? 0 : 16,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', background: 'var(--bg)',
        borderBottom: history.length > 1 ? '1px solid var(--border)' : 'none',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600, fontSize: '.88rem' }}>
          <Activity size={14} color="var(--primary)" /> Live Traffic
          {showUsername && username && (
            <span className="font-mono" style={{ fontSize: '.75rem', fontWeight: 400, color: 'var(--text-muted)' }}>
              {username}
            </span>
          )}
        </span>
        {lastOk && (
          <span role="status" style={{ fontSize: '.7rem', color: (failed || stale) ? 'var(--c-dc2626)' : 'var(--text-muted)', fontWeight: (failed || stale) ? 700 : 400 }}>
            {(failed || stale) ? `Stale · last reading ${new Date(lastOk).toLocaleTimeString('en-IN', { hour12: false })} (${ageSec}s ago)` : `Updated ${new Date(lastOk).toLocaleTimeString('en-IN', { hour12: false })}`}
          </span>
        )}
        {online !== null && online !== undefined && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.72rem' }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: online ? (active ? 'var(--cb-22c55e)' : 'var(--cb-f59e0b)') : 'var(--cb-cbd5e1)',
              display: 'inline-block',
              animation: (online && active) ? 'livePulse 1.8s ease-out infinite' : 'none',
            }} />
            <span style={{ color: online ? (active ? 'var(--c-16a34a)' : 'var(--c-92400e)') : 'var(--c-94a3b8)', fontWeight: 600 }}>
              {online ? (imbsSourced ? 'ONLINE' : (active ? 'ONLINE · ACTIVE' : 'ONLINE · IDLE')) : 'OFFLINE'}
            </span>
          </span>
        )}
      </div>

      {/* Speed panels — only for NAS devices we poll directly; Netzur-sourced status has
          no per-second rate to show (see imbsSourced block below instead) */}
      {rate && online && !imbsSourced && (
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1, padding: '14px 20px', borderRight: '1px solid var(--border)' }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>↓ Download</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--primary)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {fmtMbps(rate.rx)}
              </span>
              <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Mbps</span>
            </div>
          </div>
          <div style={{ flex: 1, padding: '14px 20px' }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--c-ea580c)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>↑ Upload</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--c-ea580c)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {fmtMbps(rate.tx)}
              </span>
              <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Mbps</span>
            </div>
          </div>
        </div>
      )}

      {/* Netzur-sourced status — this NAS can't be polled directly (e.g. older RouterOS
          with no REST API), so we only have RADIUS accounting from Netzur: accurate
          online/offline and this-session totals, updated every few minutes rather
          than live. Showing a Mbps number here would be a stale reading dressed up
          as real-time, so we don't. */}
      {rate && online && imbsSourced && (
        <div style={{ padding: '14px 20px' }}>
          <div style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
            Data used this session
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>
            {fmtBytes((rate.sessionBytes?.rx || 0) + (rate.sessionBytes?.tx || 0))}
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
            ↓{fmtBytes(rate.sessionBytes?.rx)} · ↑{fmtBytes(rate.sessionBytes?.tx)}
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 8 }}>
            Live speed isn't available for this NAS — status and totals update every few minutes via Netzur.
          </div>
        </div>
      )}

      {rate && !online && (
        <div style={{ padding: '16px 20px', color: 'var(--text-muted)', fontSize: '.85rem' }}>
          No active PPPoE session — subscriber is offline.
        </div>
      )}

      {!rate && failed && (
        <div role="alert" style={{ padding: '16px 20px', color: 'var(--c-dc2626)', fontSize: '.85rem' }}>
          Could not read live data from the NAS. Retrying every {Math.round(pollMs / 1000)}s.
        </div>
      )}
      {!rate && !failed && (
        <div style={{ padding: '16px 20px', color: 'var(--text-muted)', fontSize: '.85rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="spinner" style={{ width: 12, height: 12, flexShrink: 0 }} />
          Connecting to NAS…
        </div>
      )}

      {/* Live history chart — only shown when there are data points. Oldest point is at the
          start of the `history` array (see the `[...h, pt]` push above) and Recharts plots
          categorical data in array order, so this already scrolls left (old) → right (new)
          as points accumulate; the time axis is now shown (instead of hidden) so that
          left-to-right chronological flow actually reads as a time graph, not a sparkline. */}
      {history.length > 1 && (
        <>
        <div style={{ borderTop: '1px solid var(--border)', padding: '6px 12px 0', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: '.7rem', color: 'var(--text-muted)' }}>
          {[[30, '30 s'], [120, '2 min'], [300, '5 min']].map(([v, l]) => (
            <button key={v} type="button" style={winBtn(winSec === v)} aria-pressed={winSec === v} onClick={() => setWinSec(v)}>{l}</button>
          ))}
          <button type="button" style={winBtn(paused)} aria-pressed={paused} onClick={() => setPaused(p => !p)}>{paused ? 'Resume' : 'Pause'}</button>
          <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>Peak ↓{fmtMbps(peakRx)} · ↑{fmtMbps(peakTx)} Mbps</span>
        </div>
        <div style={{ padding: '4px 8px 4px', height: 150 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={shown} margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="lbRx" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--primary)" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="lbTx" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--cl-ea580c)" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="var(--cl-ea580c)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="time"
                tick={{ fontSize: 9, fill: 'var(--text-muted)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                minTickGap={40}
                interval="preserveStartEnd"
              />
              <YAxis hide domain={[0, 'auto']} />
              <Tooltip
                contentStyle={{ fontSize: '.75rem', borderRadius: 6, padding: '4px 8px' }}
                formatter={(v, n) => [`${fmtMbps(v)} Mbps`, n === 'rx' ? '↓ Down' : '↑ Up']}
              />
              <Area type="monotone" dataKey="rx" stroke="var(--primary)" strokeWidth={2} fill="url(#lbRx)" dot={false} isAnimationActive={false} />
              <Area type="monotone" dataKey="tx" stroke="var(--cl-ea580c)" strokeWidth={2} fill="url(#lbTx)" dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        </>
      )}
    </div>
  );
}
